import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  getProductionZone,
  ProductionZoneDetailResponse,
  updateProductionZone,
  UpdateProductionZoneBody,
} from "@/services/production-zones.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { ProductionZoneCreationForm } from "../../create/schema/production-zone-creation.schema";
import { getBooleanValue } from "@/utils/request-validations";

interface FetchProductionZoneHandlerProps {
  id: number;
  setLoadingProductionZone: Dispatch<SetStateAction<boolean>>;
  setProductionZone: Dispatch<SetStateAction<ProductionZoneDetailResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

interface UpdateProductionZoneHandlerProps {
  id: number;
  values: ProductionZoneCreationForm;
  setLoadingUpdate: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}



export const fetchProductionZoneHandler = async ({
  id,
  setLoadingProductionZone,
  setProductionZone,
  navigate,
  fetchPermissions,
}: FetchProductionZoneHandlerProps) => {
  try {
    setLoadingProductionZone(true);

    const response = await getProductionZone(id);

    setProductionZone(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTION_ZONES,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error, "No se pudo obtener la zona productiva."),
      });
    }

    throw error;
  } finally {
    setLoadingProductionZone(false);
  }
};

export const updateProductionZoneHandler = async ({
  id,
  values,
  setLoadingUpdate,
  navigate,
  fetchPermissions,
}: UpdateProductionZoneHandlerProps) => {
  const body: UpdateProductionZoneBody = {
    id,
    nombre: String(values?.nombre ?? "").trim(),
    zonaComercial: String(
      Array.isArray(values?.zonaComercial)
        ? values?.zonaComercial?.[0] ?? ""
        : values?.zonaComercial ?? "",
    ).trim(),
    activo: getBooleanValue(values?.activo),
  };

  try {
    setLoadingUpdate(true);

    await updateProductionZone(body);

    toaster.create({
      type: "success",
      title: "Zona productiva actualizada exitosamente.",
    });

    navigate(AppRoutes.PRODUCTION_ZONES);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTION_ZONES,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error, "No se pudo actualizar la zona productiva."),
      });
    }

    throw error;
  } finally {
    setLoadingUpdate(false);
  }
};
